import React, { useState, useEffect } from 'react';
import {
  Button,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { getContext, createContext, updateContext, parseDocument, listContexts } from '../../../services/api';
import {
  fetchDefaultAttachContexts,
  searchAttachContexts,
} from '../../../utils/attachSelectorFetch';
import { useAuth } from '../../../contexts/AuthContext';
import { useSessionData } from '../../../contexts/SessionDataContext';
import ContextDialog from '../../context-repository/ContextDialog';
import ContextBuilderDialog from '../../shared/context-builder/ContextBuilderDialog';
import { PRODUCT_TERMS } from '../../../constants/productTerminology';
import AttachExistingSelector from '../../shared/AttachExistingSelector';

const ContextSelector = ({ 
  open, 
  onClose, 
  onSelect,
  selectedContextIds = [],
  pinnedItems = [],
  initialEditContextId = null
}) => {
  /* eslint-disable no-unused-vars -- intentionally unused state/handlers reserved for future UI */
  const { user } = useAuth();
  const { getProfile } = useSessionData();
  const [contexts, setContexts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [selectedContexts, setSelectedContexts] = useState([]); // Changed to array for multi-select
  const [previewContext, setPreviewContext] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  
  // Context builder modal state
  const [showContextBuilder, setShowContextBuilder] = useState(false);
  const [activeBuilderTab, setActiveBuilderTab] = useState(0);
  const [isEditingContext, setIsEditingContext] = useState(false);
  const [editingContextId, setEditingContextId] = useState(null);
  const [, setSaving] = useState(false);
  const [contextData, setContextData] = useState({
    name: '',
    description: '',
    contextType: 'file_load',
    fileUrl: '',
    webUrl: '',
    content: '',
    source: '',
    sourceFileName: '',
    sourceFileType: '',
    tags: [],
    collectionId: null,
    collectionName: null,
    permissions: {
      accessLevel: 'INDIVIDUAL',
      companyAccess: false,
      teamAccess: [],
      groupAccess: [],
      individualAccess: []
    }
  });
  const [contextPreview, setContextPreview] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const builderSteps = [
    { label: 'Create Knowledge' },
    { label: 'Knowledge Info & Publish' }
  ];

  // Hydrate user profile when dialog opens (session cache)
  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const profileData = await getProfile({ force: false });
        if (cancelled) return;
        if (!profileData?.companyId && user?.companyId) {
          setUserProfile({
            ...profileData,
            id: user.id,
            companyId: user.companyId,
            teamId: user.teamId
          });
        } else {
          setUserProfile(profileData);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        if (!cancelled && user) {
          setUserProfile({
            id: user.id,
            companyId: user.companyId,
            teamId: user.teamId
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user, getProfile]);

  // Load contexts when dialog opens — only needed after create/update for local cache
  // AttachExistingSelector handles list fetching via onFetchDefaultItems

  // Handle initial edit context ID - load context and open edit mode
  useEffect(() => {
    if (!open || !initialEditContextId || editingContextId) return;
    
    const loadAndEditContext = async () => {
      try {
        // Fetch the context data
        const freshContext = await getContext(initialEditContextId);
        const contextDataToEdit = freshContext.context || freshContext;
        
        setEditingContextId(contextDataToEdit.id);
        
        // Determine context type based on existing data
        let contextType = 'direct_input';
        if (contextDataToEdit.source === 'UPLOADED_FILE') {
          contextType = 'file_load';
        } else if (contextDataToEdit.source === 'WEB_URL') {
          contextType = 'web';
        }
        
        // Populate contextData with existing context data
        setContextData({
          id: contextDataToEdit.id,
          name: contextDataToEdit.name || '',
          description: contextDataToEdit.description || '',
          contextType: contextType,
          fileUrl: contextDataToEdit.sourceFileName || '',
          webUrl: contextDataToEdit.webUrl || '',
          content: contextDataToEdit.content || '',
          source: contextDataToEdit.source || 'MANUAL_ENTRY',
          sourceFileName: contextDataToEdit.sourceFileName || '',
          sourceFileType: contextDataToEdit.sourceFileType || '',
          tags: contextDataToEdit.tags || [],
          permissions: contextDataToEdit.permissions || {
            accessLevel: 'INDIVIDUAL',
            companyAccess: false,
            teamAccess: [],
            groupAccess: [],
            individualAccess: []
          },
          collectionId: contextDataToEdit.collectionId || null,
          collectionName: contextDataToEdit.collectionName || null
        });
        
        // Set preview
        if (contextDataToEdit.content) {
          setContextPreview(contextDataToEdit.content);
        }
        
        // Open context builder modal for editing
        setIsEditingContext(true);
        setShowContextBuilder(true);
        setActiveBuilderTab(0);
      } catch (err) {
        console.error('Error loading context for edit:', err);
        alert('Failed to load knowledge for editing');
      }
    };
    
    loadAndEditContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editingContextId intentionally omitted
  }, [open, initialEditContextId]);

  // Reset builder state when closing dialog
  useEffect(() => {
    if (!open) {
      // Reset when dialog closes
      setActiveBuilderTab(0);
      setContextData({
        name: '',
        description: '',
        contextType: 'file_load',
        fileUrl: '',
        webUrl: '',
        content: '',
        source: '',
        sourceFileName: '',
        sourceFileType: '',
        tags: [],
        collectionId: null,
        collectionName: null,
        permissions: {
          companyAccess: false,
          teamAccess: [],
          groupAccess: [],
          individualAccess: [],
          canEdit: [],
          canShare: [],
          canDelete: []
        }
      });
      setContextPreview('');
      setUploadSuccess(false);
      setEditingContextId(null);
      setShowContextBuilder(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editingContextId intentionally omitted to avoid reset loop
  }, [open]);

  const loadContexts = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load all accessible contexts (shared scope)
      const response = await listContexts('shared', {});
      const loadedContexts = response.contexts || response.data || [];
      setContexts(loadedContexts);
    } catch (err) {
      console.error('Error loading contexts:', err);
      setError('Failed to load knowledge');
    } finally {
      setLoading(false);
    }
  };

  const handleContextSelect = (context) => {
    // Toggle context selection (add if not selected, remove if already selected)
    setSelectedContexts(prev => {
      const isSelected = prev.some(ctx => ctx.id === context.id);
      if (isSelected) {
        return prev.filter(ctx => ctx.id !== context.id);
      } else {
        return [...prev, context];
      }
    });
  };

  const handleConfirmSelection = async (additions = [], selectedIds = null, selectedItems = null) => {
    const sourceContexts = Array.isArray(selectedItems) && selectedItems.length > 0
      ? selectedItems
      : (Array.isArray(selectedIds)
        ? pinnedItems.filter((context) => selectedIds.includes(context.id))
        : selectedContexts);
    const contextFiles = sourceContexts.map((context) => ({
      id: context.id,
      contextId: context.id,
      name: context.name,
      type: context.sourceFileType || 'text/plain',
      size: (context.content || '').length,
      contextName: context.name,
      description: context.description || '',
      content: context.content,
      category: context.category,
      fromContextLibrary: true,
    }));

    if (onSelect) {
      await onSelect(contextFiles);
    }

    handleClose();
  };

  const handleClose = () => {
    setSelectedContexts([]);
    setError(null);
    setEditingContextId(null);
    onClose();
  };

  const handleCloseContextBuilder = () => {
    setShowContextBuilder(false);
    setIsEditingContext(false);
    setEditingContextId(null);
    setActiveBuilderTab(0);
    // Reset form
    setContextData({
      name: '',
      description: '',
      contextType: 'file_load',
      fileUrl: '',
      webUrl: '',
      content: '',
      source: '',
      sourceFileName: '',
      sourceFileType: '',
      tags: [],
      collectionId: null,
      collectionName: null,
      permissions: {
        companyAccess: false,
        teamAccess: [],
        groupAccess: [],
        individualAccess: [],
        canEdit: [],
        canShare: [],
        canDelete: []
      }
    });
    setContextPreview('');
    setUploadError('');
    setUploadSuccess(false);
    setIsUploading(false);
  };

  const handleCreateContextClick = () => {
    // Reset context data for new creation
    setContextData({
      name: '',
      description: '',
      contextType: 'file_load',
      fileUrl: '',
      webUrl: '',
      content: '',
      source: '',
      sourceFileName: '',
      sourceFileType: '',
      tags: [],
      collectionId: null,
      collectionName: null,
      permissions: {
        companyAccess: false,
        teamAccess: [],
        groupAccess: [],
        individualAccess: [],
        canEdit: [],
        canShare: [],
        canDelete: []
      }
    });
    setContextPreview('');
    setUploadError('');
    setUploadSuccess(false);
    setIsUploading(false);
    setIsEditingContext(false);
    setEditingContextId(null);
    setActiveBuilderTab(0);
    setShowContextBuilder(true);
  };

  // Check if user can edit a context
  const canEditContext = (context) => {
    if (!user || !context) return false;
    const isCreator = context.userId === user.id;
    const isAdmin = user.role === 'ADMIN' || user.role === 'OWNER';
    const permissions = context.permissions || {};
    const editablePrincipalIds = [user.id, user.teamId].filter(Boolean);
    const hasExplicitEditAccess = [permissions.canEdit, permissions.canDelete]
      .filter(Array.isArray)
      .some((principalIds) => principalIds.some((id) => editablePrincipalIds.includes(id)));
    return isCreator || isAdmin || hasExplicitEditAccess;
  };

  const handleEditContext = async (context) => {
    try {
      // Close preview dialog
      setShowPreviewDialog(false);
      
      // Fetch fresh context data from API
      const freshContext = await getContext(context.id);
      const contextDataToEdit = freshContext.context || freshContext;
      
      setEditingContextId(contextDataToEdit.id);
      
      // Determine context type based on existing data
      let contextType = 'direct_input';
      if (contextDataToEdit.source === 'UPLOADED_FILE') {
        contextType = 'file_load';
      } else if (contextDataToEdit.source === 'WEB_URL') {
        contextType = 'web';
      }
      
      // Populate contextData with existing context data
      setContextData({
        id: contextDataToEdit.id,
        name: contextDataToEdit.name || '',
        description: contextDataToEdit.description || '',
        contextType: contextType,
        fileUrl: contextDataToEdit.sourceFileName || '',
        webUrl: contextDataToEdit.webUrl || '',
        content: contextDataToEdit.content || '',
        source: contextDataToEdit.source || 'MANUAL_ENTRY',
        sourceFileName: contextDataToEdit.sourceFileName || '',
        sourceFileType: contextDataToEdit.sourceFileType || '',
        tags: contextDataToEdit.tags || [],
        permissions: contextDataToEdit.permissions || {
          accessLevel: 'INDIVIDUAL',
          companyAccess: false,
          teamAccess: [],
          groupAccess: [],
          individualAccess: []
        },
        collectionId: contextDataToEdit.collectionId || null,
        collectionName: contextDataToEdit.collectionName || null
      });
      
      // Set preview
      if (contextDataToEdit.content) {
        setContextPreview(contextDataToEdit.content);
      }
      
      // Open context builder modal for editing
      setIsEditingContext(true);
      setShowContextBuilder(true);
      setActiveBuilderTab(0);
    } catch (err) {
      console.error('Error loading context for edit:', err);
      alert('Failed to load knowledge for editing');
    }
  };

  // Context creation handlers
  const handleContextInputChange = (field, value) => {
    setContextData(prev => ({ ...prev, [field]: value }));
  };

  const handleContextTypeChange = (value) => {
    // Value is passed directly from BasicInfoTab (not an event object)
    handleContextInputChange('contextType', value);
    setContextPreview('');
    setUploadError('');
    setUploadSuccess(false);
    handleContextInputChange('content', '');
    handleContextInputChange('sourceFileName', '');
    handleContextInputChange('sourceFileType', '');
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'text/plain',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/pdf',
      'text/markdown',
      'text/html',
      'text/csv',
      'application/json'
    ];
    const allowedExtensions = ['.txt', '.docx', '.pdf', '.md', '.markdown', '.html', '.htm', '.csv', '.json'];
    const nameLower = (file.name || '').toLowerCase();
    const dotExt = nameLower.includes('.') ? nameLower.slice(nameLower.lastIndexOf('.')) : '';
    const typeOk = allowedTypes.includes(file.type) || allowedExtensions.includes(dotExt);
    if (!typeOk) {
      setUploadError('Unsupported file type. Please upload TXT, DOCX, PDF, Markdown, HTML, CSV, or JSON files.');
      return;
    }

    setIsUploading(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64 = e.target.result;
          const result = await parseDocument(base64, file.name, file.type);
          const parsedText = result?.text || '';
          
          handleContextInputChange('fileUrl', file.name);
          handleContextInputChange('content', parsedText);
          handleContextInputChange('source', 'UPLOADED_FILE');
          handleContextInputChange('sourceFileName', file.name);
          handleContextInputChange('sourceFileType', file.type);
          setContextPreview(parsedText);
          setUploadSuccess(true);
        } catch (err) {
          console.error('Error parsing file:', err);
          setUploadError(err?.response?.data?.message || err?.message || 'Failed to parse file. Please try again.');
        } finally {
          setIsUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error reading file:', err);
      setUploadError('Failed to read file. Please try again.');
      setIsUploading(false);
    }
  };

  const handleContentChange = (value) => {
    // Value is passed directly from BasicInfoTab (not an event object)
    handleContextInputChange('content', value);
    setContextPreview(value);
  };

  const handleGenerateSummary = () => {
    // Premium feature - placeholder
  };

  const handleBuilderTabChange = (newTab) => {
    setActiveBuilderTab(newTab);
  };

  const handleNextBuilderTab = () => {
    if (activeBuilderTab < builderSteps.length - 1) {
      setActiveBuilderTab(prev => prev + 1);
    }
  };

  const handlePreviousBuilderTab = () => {
    if (activeBuilderTab > 0) {
      setActiveBuilderTab(prev => prev - 1);
    }
  };

  const handleSaveContext = async (contextDataToSave, contextPreviewToSave, isEditing, editingContextIdToSave) => {
    try {
      setSaving(true);
      const companyId = user?.companyId || user?.company?.id;
      if (!companyId) {
        alert('Missing company. Please re-login or ensure your profile has a company.');
        throw new Error('Missing company');
      }

      const contentToCheck = contextDataToSave.content || contextPreviewToSave || '';
      const isInlineOnlyContext =
        contextDataToSave.contextType === 'direct_input' &&
        !['UPLOADED_FILE', 'WEB_URL'].includes(contextDataToSave.source || '');
      if (isInlineOnlyContext && contentToCheck.length > 120000) {
        alert('Knowledge content is too large. Please reduce the content or split into multiple knowledge items.');
        throw new Error('Content too large');
      }

      // Ensure permissions are properly structured
      const permissions = contextDataToSave.permissions || {
        accessLevel: 'INDIVIDUAL',
        companyAccess: false,
        teamAccess: [],
        groupAccess: [],
        individualAccess: []
      };
      
      const finalPermissions = {
        accessLevel: permissions.accessLevel || 'INDIVIDUAL',
        companyAccess: permissions.companyAccess || false,
        teamAccess: Array.isArray(permissions.teamAccess) ? permissions.teamAccess : [],
        groupAccess: Array.isArray(permissions.groupAccess) ? permissions.groupAccess : [],
        individualAccess: Array.isArray(permissions.individualAccess) ? permissions.individualAccess : [],
        canEdit: Array.isArray(permissions.canEdit) ? permissions.canEdit : [],
        canShare: Array.isArray(permissions.canShare) ? permissions.canShare : [],
        canDelete: Array.isArray(permissions.canDelete) ? permissions.canDelete : []
      };

      const payload = {
        name: contextDataToSave.name,
        description: contextDataToSave.description,
        content: contextDataToSave.content || contextPreviewToSave || '',
        source: contextDataToSave.source || (contextDataToSave.contextType === 'file_load' ? 'UPLOADED_FILE' : (contextDataToSave.contextType === 'web' ? 'WEB_URL' : 'MANUAL_ENTRY')),
        sourceFileName: contextDataToSave.sourceFileName || '',
        sourceFileType: contextDataToSave.sourceFileType || '',
        webUrl: contextDataToSave.webUrl || '',
        tags: contextDataToSave.tags || [],
        category: contextDataToSave.category || 'General',
        collectionId: contextDataToSave.collectionId || null,
        collectionName: contextDataToSave.collectionName || null,
        permissions: finalPermissions
      };

      const wasEditing = !!editingContextIdToSave;
      
      if (editingContextIdToSave) {
        // Update existing context
        const result = await updateContext(editingContextIdToSave, payload);
        const updated = result?.context || result?.data?.context || result;
        
        if (updated && updated.id) {
          // Context updated successfully - reload contexts
          await loadContexts();
        }
        // Stay open - reset editing state
        setEditingContextId(null);
        setContextData({
          name: '',
          description: '',
          contextType: 'file_load',
          fileUrl: '',
          webUrl: '',
          content: '',
          source: '',
          sourceFileName: '',
          sourceFileType: '',
          tags: [],
          collectionId: null,
          collectionName: null,
          permissions: {
            accessLevel: 'INDIVIDUAL',
            companyAccess: false,
            teamAccess: [],
            groupAccess: [],
            individualAccess: []
          }
        });
        setContextPreview('');
        setUploadSuccess(false);
      } else {
        // Create new context
        const createPayload = {
          ...payload,
          contextType: contextDataToSave.contextType,
          companyId,
          teamId: user?.teamId || null
        };
        const result = await createContext(createPayload);
        const created = result?.context || result?.data?.context || result;

        // Add the newly created context to the prompt
        if (created && created.id) {
          const contextFile = {
            id: created.id,
            contextId: created.id,
            name: created.name,
            type: created.sourceFileType || 'text/plain',
            size: (created.content || '').length,
            contextName: created.name,
            description: created.description || '',
            content: created.content,
            category: created.category,
            fromContextLibrary: true,
          };
          const batch = [
            ...selectedContexts.map((context) => ({
              id: context.id,
              contextId: context.id,
              name: context.name,
              type: context.sourceFileType || 'text/plain',
              size: (context.content || '').length,
              contextName: context.name,
              description: context.description || '',
              content: context.content,
              category: context.category,
              fromContextLibrary: true,
            })),
            contextFile,
          ];
          const seen = new Set();
          const deduped = batch.filter((f) => {
            const id = f.contextId || f.id;
            if (!id || seen.has(id)) return false;
            seen.add(id);
            return true;
          });
          onSelect(deduped);
        }
        
        // Close the selector dialog after creating and selecting
        handleClose();
      }
      
      // Reload contexts
      await loadContexts();
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || (editingContextIdToSave ? 'Failed to update knowledge' : 'Failed to create knowledge');
      alert(message);
      throw err; // Re-throw so the shared component knows it failed
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AttachExistingSelector
        open={open}
        onClose={handleClose}
        title="Add knowledge"
        itemKind="context"
        selectedIds={selectedContextIds}
        pinnedItems={pinnedItems}
        onFetchDefaultItems={fetchDefaultAttachContexts}
        onSearchItems={searchAttachContexts}
        searchPlaceholder="Search knowledge..."
        emptyText={error || 'No knowledge available'}
        getPrimaryText={(context) => context.name || PRODUCT_TERMS.knowledge.untitled}
        getSecondaryText={(context) => context.description || 'No description'}
        getTags={(context) => context.tags || []}
        confirmAllSelected
        libraryMode
        selectionUnitLabel="knowledge"
        confirmLabel={({ selectedCount }) => (
          selectedCount === 0
            ? 'Apply (no knowledge)'
            : `Apply ${selectedCount} knowledge item${selectedCount !== 1 ? 's' : ''}`
        )}
        onPreview={(context) => {
          setPreviewContext(context);
          setShowPreviewDialog(true);
        }}
        createAction={(
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreateContextClick}
          >
            Create Knowledge
          </Button>
        )}
        onConfirm={handleConfirmSelection}
      />

      {/* Context Builder Dialog */}
      <ContextBuilderDialog
        open={showContextBuilder}
        onClose={handleCloseContextBuilder}
        isEditing={isEditingContext}
        editingContextId={editingContextId}
        initialContextData={isEditingContext && editingContextId ? contextData : null}
        userProfile={userProfile}
        onSave={async ({ contextData: savedContextData, contextPreview: savedContextPreview, isEditing, editingContextId: savedEditingContextId }) => {
          await handleSaveContext(savedContextData, savedContextPreview, isEditing, savedEditingContextId);
        }}
      />

      {/* Context Preview Dialog - Using standard ContextDialog with select/deselect and edit */}
      <ContextDialog
        open={showPreviewDialog}
        context={previewContext}
        onClose={() => setShowPreviewDialog(false)}
        onSelect={handleContextSelect}
        isSelected={previewContext ? selectedContexts.some(ctx => ctx.id === previewContext.id) : false}
        onEdit={handleEditContext}
        canEdit={previewContext ? canEditContext(previewContext) : false}
      />
    </>
  );
};

export default ContextSelector;
